import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Building2, Hash, CalendarDays } from "lucide-react";
import { SITE, IMAGES } from "@/lib/site";

export const AboutContact = () => {
    const details = [
        { icon: CalendarDays, label: "Established", value: SITE.established },
        { icon: Hash, label: "Registration No. (CIN)", value: SITE.cin },
        { icon: Building2, label: "Company", value: SITE.name },
    ];

    return (
        <>
            <section id="about" data-testid="about-section" className="relative py-28 sm:py-32 px-5 sm:px-8">
                <div className="mx-auto max-w-7xl grid lg:grid-cols-2 gap-14 items-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="relative"
                    >
                        <div className="relative overflow-hidden rounded-3xl ring-1 ring-white/10">
                            <img src={IMAGES.corporate} alt="Hiring handshake" className="h-[440px] w-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/80 to-transparent" />
                        </div>
                        <div className="absolute -bottom-6 -right-4 sm:right-6 glass rounded-2xl p-5 flex items-center gap-4">
                            <img src={SITE.logo} alt="logo" className="h-14 w-14 rounded-full object-cover" />
                            <div>
                                <p className="font-display font-bold text-white leading-tight">Bhoir Group</p>
                                <p className="text-xs text-slate-400">Trusted since {SITE.established}</p>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="text-xs tracking-[0.35em] text-[#F47B20] uppercase font-semibold">
                            About Us
                        </span>
                        <h2 className="mt-5 font-display text-4xl sm:text-5xl font-extrabold text-white leading-tight">
                            Building careers, powering businesses
                        </h2>
                        <p className="mt-6 text-slate-400 leading-relaxed">
                            Bhoir Group of Services is a registered staffing and placement
                            company based in Pimpri Chinchwad, Pune. We bridge the gap between
                            capable candidates — from ITI and Diploma holders to graduates and
                            first-time workers — and companies seeking dependable talent.
                        </p>
                        <div className="mt-8 grid sm:grid-cols-3 gap-4">
                            {details.map((d) => {
                                const Icon = d.icon;
                                return (
                                    <div key={d.label} className="glass rounded-xl p-4">
                                        <Icon size={18} className="text-[#F47B20] mb-2" />
                                        <p className="text-[11px] uppercase tracking-wider text-slate-500">{d.label}</p>
                                        <p className="text-sm font-semibold text-white break-words">{d.value}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </motion.div>
                </div>
            </section>

            <section id="contact" data-testid="contact-section" className="relative py-24 px-5 sm:px-8 bg-[#0A0F1C] border-y border-white/10">
                <div className="mx-auto max-w-7xl">
                    <div className="text-center mb-14">
                        <span className="text-xs tracking-[0.35em] text-[#F47B20] uppercase font-semibold">Reach Us</span>
                        <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold text-white">Get in touch</h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="glass rounded-2xl p-7" data-testid="contact-phones">
                            <Phone className="text-[#F47B20] mb-4" />
                            <h3 className="font-display text-lg font-bold text-white mb-3">Call Us</h3>
                            {SITE.phones.map((p) => (
                                <a key={p} href={`tel:${p}`} className="block text-slate-300 hover:text-[#F47B20] transition-colors">
                                    +91 {p}
                                </a>
                            ))}
                        </div>
                        <div className="glass rounded-2xl p-7" data-testid="contact-emails">
                            <Mail className="text-[#F47B20] mb-4" />
                            <h3 className="font-display text-lg font-bold text-white mb-3">Email Us</h3>
                            {SITE.emails.map((em) => (
                                <a key={em} href={`mailto:${em}`} className="block text-slate-300 hover:text-[#F47B20] transition-colors break-all">
                                    {em}
                                </a>
                            ))}
                        </div>
                        <div className="glass rounded-2xl p-7" data-testid="contact-address">
                            <MapPin className="text-[#F47B20] mb-4" />
                            <h3 className="font-display text-lg font-bold text-white mb-3">Visit Us</h3>
                            <p className="text-slate-300 leading-relaxed text-sm">
                                {SITE.address}, {SITE.district}, {SITE.state} — {SITE.pin}
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};
